import { token } from '@atlaskit/tokens';
import { type Options } from '@atlassiansox/cross-flow-support';
import { css } from '@compiled/react';
import { useCrossflowMessageClicked } from '@post-office/components--crossflow-cta-button';
import { useMessageContext } from '@atlassian/post-office-context/message-context';
import React, { type ComponentProps } from 'react';

import { Actions } from './components/actions';
import { type JSX } from 'react/jsx-runtime';

export type Props = {
	bodyContent: React.ReactNode;
	ctaContent: React.ReactNode;
	crossflowOptions: Options;
	onDismissed?: (messageInstanceId: string) => void;
	whyAmISeeingThisModalContent?: ComponentProps<typeof Actions>['whyAmISeeingThisModalContent'];
	extraAnalyticsAttributes?: Record<string, string>;
};

const confluenceBannerMessageStylesCss = css({
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	borderRadius: token('radius.large', '8px'),
	backgroundColor: token('color.background.accent.blue.subtlest', '#DEEBFF'),
	minHeight: token('space.500', '90px'),
	paddingInline: token('space.600', '48px'),
	paddingBlock: token('space.300', '24px'),
	'@media (max-width: 1024px)': {
		padding: token('space.200', '16px'),
		paddingLeft: token('space.300', '24px'),
	},
	zIndex: 0,
	marginBottom: token('space.300', '24px'),
	'&:hover': {
		cursor: 'pointer',
		outline: `1px solid ${token('color.background.accent.blue.subtle.hovered')}`,
	},
});

export const ConfluenceBannerMessage = ({
	onDismissed,
	whyAmISeeingThisModalContent,
	bodyContent,
	ctaContent,
	crossflowOptions,
}: Props): JSX.Element => {
	const { messageInstanceId } = useMessageContext();
	const { handleClick } = useCrossflowMessageClicked(crossflowOptions, 'recommendation-message');

	if (!messageInstanceId) {
		throw new Error('messageInstanceId is required');
	}

	const handleMessageClick = (
		e: React.MouseEvent<HTMLElement> | React.MouseEvent<HTMLButtonElement>,
	) => {
		const currentTarget = e.currentTarget as HTMLElement | HTMLButtonElement;

		let target = e.target as HTMLElement | HTMLButtonElement;
		while (target.parentNode) {
			if (
				target.tagName.toLowerCase() === 'button' ||
				target.tagName.toLowerCase() === 'a' ||
				target.tagName.toLowerCase() === 'html' // this is for the modal & its transparent background
			) {
				// Don't open Cross-flow since these child elements should've already handled the click
				return;
			}

			// Open Cross-flow if the click is on the banner message itself
			if (target === currentTarget) {
				handleClick();
				return;
			}

			// Keep traversing up the DOM tree
			target = target.parentNode as HTMLElement | HTMLButtonElement;
		}
	};

	return (
		// eslint-disable-next-line @atlassian/a11y/no-static-element-interactions, @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/interactive-element-not-keyboard-focusable
		<div
			data-testid="confluence-banner-message"
			css={confluenceBannerMessageStylesCss}
			onClick={handleMessageClick}
		>
			{bodyContent}
			{ctaContent}
			<Actions
				whyAmISeeingThisModalContent={whyAmISeeingThisModalContent}
				onDismissed={() => onDismissed?.(messageInstanceId)}
			/>
		</div>
	);
};
